/**
 * FhirMedicationListWidget — Medication summary for subject dashboard (DRR-321)
 *
 * Shows active medications from FHIR MedicationRequest resources.
 * Widget version of the medication display from FhirClinicalPanel.
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Card,
  Skeleton,
  Alert,
  Box,
  ScrollArea,
} from '@mantine/core';
import { IconPill, IconAlertCircle, IconClock } from '@tabler/icons-react';
import { useFhirMedicationsBySubjectQuery } from '@ferp/core/generated/graphql';
import type { WidgetProps } from '@ferp/core';

interface ParsedMedication {
  id: string;
  display: string;
  status: string;
  intent: string;
  dosage?: string;
  frequency?: string;
  route?: string;
  authoredOn?: string;
}

function parseMedication(resourceId: string, json: string): ParsedMedication | null {
  try {
    const f = JSON.parse(json) as Record<string, unknown>;

    const medCodeableConcept = f.medicationCodeableConcept as { text?: string; coding?: Array<{ display?: string }> } | undefined;
    const display = medCodeableConcept?.text ?? medCodeableConcept?.coding?.[0]?.display ?? 'Unknown medication';

    const dosageInstructions = f.dosageInstruction as Array<{
      text?: string;
      timing?: { repeat?: { frequency?: number; period?: number; periodUnit?: string } };
      route?: { text?: string; coding?: Array<{ display?: string }> };
      doseAndRate?: Array<{ doseQuantity?: { value?: number; unit?: string } }>;
    }> | undefined;

    const firstDosage = dosageInstructions?.[0];
    let dosage: string | undefined;
    let frequency: string | undefined;
    let route: string | undefined;

    if (firstDosage) {
      const doseQty = firstDosage.doseAndRate?.[0]?.doseQuantity;
      if (doseQty) {
        dosage = `${doseQty.value} ${doseQty.unit ?? ''}`.trim();
      }

      const timing = firstDosage.timing?.repeat;
      if (timing) {
        frequency = `${timing.frequency ?? 1}x per ${timing.period ?? 1} ${timing.periodUnit ?? 'd'}`;
      } else if (firstDosage.text) {
        frequency = firstDosage.text;
      }

      route = firstDosage.route?.text ?? firstDosage.route?.coding?.[0]?.display;
    }

    return {
      id: resourceId,
      display,
      status: (f.status as string) ?? 'unknown',
      intent: (f.intent as string) ?? 'order',
      dosage,
      frequency,
      route,
      authoredOn: f.authoredOn as string | undefined,
    };
  } catch {
    return null;
  }
}

const STATUS_COLOR: Record<string, string> = {
  active: 'green',
  'on-hold': 'yellow',
  completed: 'blue',
  cancelled: 'gray',
  stopped: 'red',
  draft: 'orange',
  'entered-in-error': 'red',
};

export function FhirMedicationListWidget({ subjectActorId }: WidgetProps) {
  const { data, loading, error } = useFhirMedicationsBySubjectQuery({
    variables: { subjectActorId: subjectActorId ?? '' },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const medications = useMemo(() => {
    const raw = data?.fhirMedicationsBySubject ?? [];
    return raw
      .filter(r => r.isLatest !== false)
      .map(r => parseMedication(r.resourceId, r.fhirJson))
      .filter((m): m is ParsedMedication => m !== null);
  }, [data]);

  if (loading && medications.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Error loading medications.
      </Alert>
    );
  }

  if (medications.length === 0) {
    return (
      <Alert icon={<IconPill size={16} />} color="blue" variant="light">
        No medications found for this subject.
      </Alert>
    );
  }

  const activeMeds = medications.filter(m => m.status === 'active');
  const otherMeds = medications.filter(m => m.status !== 'active');

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <Text fw={600} size="sm">Medications</Text>
        <Badge size="sm" color="green">{activeMeds.length} Active</Badge>
        {otherMeds.length > 0 && (
          <Badge size="sm" variant="light">{otherMeds.length} Other</Badge>
        )}
      </Group>

      <ScrollArea.Autosize mah={300}>
        <Stack gap="xs">
          {activeMeds.map((med) => (
            <Card key={med.id} withBorder padding="xs" radius="sm">
              <Group justify="space-between" wrap="nowrap">
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Group gap="xs" wrap="nowrap">
                    <IconPill size={14} color="var(--mantine-color-blue-6)" />
                    <Text fw={500} size="sm" lineClamp={1}>{med.display}</Text>
                  </Group>
                  <Group gap="xs" mt={4}>
                    {med.dosage && (
                      <Badge size="xs" variant="light" color="blue">{med.dosage}</Badge>
                    )}
                    {med.frequency && (
                      <Group gap={2}>
                        <IconClock size={10} color="gray" />
                        <Text size="xs" c="dimmed">{med.frequency}</Text>
                      </Group>
                    )}
                    {med.route && (
                      <Text size="xs" c="dimmed">{med.route}</Text>
                    )}
                  </Group>
                </Box>
                <Badge size="xs" color={STATUS_COLOR[med.status] ?? 'gray'}>
                  {med.status}
                </Badge>
              </Group>
            </Card>
          ))}

          {otherMeds.length > 0 && (
            <Text size="xs" c="dimmed" ta="center">
              +{otherMeds.length} inactive medication{otherMeds.length !== 1 ? 's' : ''}
            </Text>
          )}
        </Stack>
      </ScrollArea.Autosize>
    </Stack>
  );
}
